import { Link } from "react-router-dom";
import { CellTower, ArrowRight, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";

const CERTIFICATIONS = [
  {
    code: "BST",
    long: {
      pt: "Formação Básica de Segurança (Basic Safety Training)",
      en: "Basic Safety Training",
      es: "Formación Básica de Seguridad (Basic Safety Training)",
    },
  },
  {
    code: "HUET",
    long: {
      pt: "Treino de Escape Subaquático de Helicóptero (Helicopter Underwater Escape Training)",
      en: "Helicopter Underwater Escape Training",
      es: "Entrenamiento de Escape Subacuático de Helicóptero (Helicopter Underwater Escape Training)",
    },
  },
  {
    code: "TRA",
    long: {
      pt: "Técnicas de Resgate e Auto-Resgate (Rescue and Self-Rescue Techniques) — comuns em contexto de salvamento",
      en: "Rescue and Self-Rescue Techniques — common in emergency rescue contexts",
      es: "Técnicas de Rescate y Auto-Rescate (Rescue and Self-Rescue Techniques) — habituales en contextos de salvamento",
    },
  },
];

const COPY = {
  pt: {
    overline: "Projecto · Implementação · Manutenção",
    title:
      "Torres de Telecomunicações e Mastros — projecto, implementação e manutenção de 6 a 30 metros.",
    desc:
      "Da residência ao campo de mineração, projectamos, construímos e mantemos mastros galvanizados e torres treliçadas autoportantes ou estaiadas — com base estrutural calculada, ancoragens em betão armado, instalação de antenas/dishes e protecção contra descargas. Coberturas em Luanda e em todas as províncias.",
    bullets: [
      "Projecto, implementação e manutenção de torres",
      "Mastros galvanizados de 6m a 12m (residencial / PME)",
      "Torres treliçadas autoportantes 15m a 30m (comercial / industrial)",
      "Torres estaiadas (guyed) para sites remotos e mineração",
      "Plataformas Offshore — Petróleo, Gás e Energia Eólica",
      "Estudo estrutural, ancoragens, balizamento e pára-raios",
    ],
    cta_quote: "Pedir orçamento",
    cta_detail: "Ver serviço completo",
    gallery_alt: (h) => `Torre de ${h} metros instalada pela TVCabSat`,
    certs_title: "Certificações da equipa",
    certs_note:
      "As certificações BST, HUET e TRA são essenciais para os profissionais que trabalham em ambientes de alto risco, como as indústrias Offshore (Petróleo e Gás) e Energia Eólica.",
  },
  en: {
    overline: "Design · Build · Maintenance",
    title:
      "Telecom Towers & Masts — design, build and maintenance from 6 to 30 metres.",
    desc:
      "From residential rooftops to mining sites, we design, build and maintain galvanised masts and self-supporting or guyed lattice towers — with calculated structural bases, reinforced concrete anchors, antenna/dish mounting and lightning protection. Coverage across Luanda and all Angolan provinces.",
    bullets: [
      "Tower design, build and maintenance",
      "Galvanised masts from 6m to 12m (residential / SMB)",
      "Self-supporting lattice towers 15m to 30m (commercial / industrial)",
      "Guyed towers for remote and mining sites",
      "Offshore Platforms — Oil, Gas and Wind Energy",
      "Structural study, anchors, obstruction lighting, lightning protection",
    ],
    cta_quote: "Request a quote",
    cta_detail: "See full service",
    gallery_alt: (h) => `${h}-metre tower installed by TVCabSat`,
    certs_title: "Team certifications",
    certs_note:
      "BST, HUET and TRA certifications are essential for professionals operating in high-risk environments such as Offshore (Oil & Gas) and Wind Energy industries.",
  },
  es: {
    overline: "Proyecto · Implementación · Mantenimiento",
    title:
      "Torres de Telecomunicaciones y Mástiles — proyecto, implementación y mantenimiento de 6 a 30 metros.",
    desc:
      "De la residencia al campo minero, proyectamos, construimos y mantenemos mástiles galvanizados y torres reticuladas autoportantes o con vientos — con base estructural calculada, anclajes en hormigón armado, instalación de antenas/dishes y protección contra rayos. Cobertura en Luanda y todas las provincias.",
    bullets: [
      "Proyecto, implementación y mantenimiento de torres",
      "Mástiles galvanizados de 6m a 12m (residencial / pyme)",
      "Torres reticuladas autoportantes 15m a 30m (comercial / industrial)",
      "Torres con vientos para sitios remotos y minería",
      "Plataformas Offshore — Petróleo, Gas y Energía Eólica",
      "Estudio estructural, anclajes, balizamiento y pararrayos",
    ],
    cta_quote: "Solicitar presupuesto",
    cta_detail: "Ver servicio completo",
    gallery_alt: (h) => `Torre de ${h} metros instalada por TVCabSat`,
    certs_title: "Certificaciones del equipo",
    certs_note:
      "Las certificaciones BST, HUET y TRA son fundamentales para profesionales que actúan en ambientes de alto riesgo, como las industrias Offshore (Petróleo y Gas) y de Energía Eólica.",
  },
};

const TILES = [
  { src: "/towers/tower-6m.png", height: 6, span: "md:col-span-3" },
  { src: "/towers/tower-12m.png", height: 12, span: "md:col-span-3" },
  { src: "/towers/tower-24m.png", height: 24, span: "md:col-span-3" },
  { src: "/towers/tower-30m.png", height: 30, span: "md:col-span-3" },
  {
    src: "/towers/tower-residential.png",
    label: { pt: "Residencial", en: "Residential", es: "Residencial" },
    span: "md:col-span-3",
  },
  {
    src: "/towers/tower-commercial.png",
    label: { pt: "Comercial", en: "Commercial", es: "Comercial" },
    span: "md:col-span-3",
  },
  {
    src: "/towers/tower-mining.png",
    label: { pt: "Mineração", en: "Mining", es: "Minería" },
    span: "md:col-span-3",
  },
  {
    src: "/towers/tower-broadcast.png",
    label: { pt: "Broadcast", en: "Broadcast", es: "Broadcast" },
    span: "md:col-span-3",
  },
  {
    src: "/towers/tower-offshore-soyo.png",
    label: {
      pt: "Plataforma Offshore — Soyo",
      en: "Offshore Platform — Soyo",
      es: "Plataforma Offshore — Soyo",
    },
    span: "md:col-span-6",
    aspect: "aspect-[3/2]",
  },
  {
    src: "/towers/tower-rig-offshore-soyo.png",
    label: {
      pt: "Rig Offshore — Soyo",
      en: "Offshore Rig — Soyo",
      es: "Rig Offshore — Soyo",
    },
    span: "md:col-span-6",
    aspect: "aspect-[3/2]",
  },
];

export const TowersShowcase = ({ showDetailCta = true }) => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.pt;

  return (
    <section
      id="towers"
      data-testid="towers-showcase"
      className="relative scroll-mt-[160px] border-y border-slate-200 bg-gradient-to-b from-[#dfecf6] via-[#d8e8f5] to-[#d6e6f2] py-20 lg:py-28"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-blue-700">
              <CellTower size={12} weight="fill" />
              {c.overline}
            </div>
            <h2
              data-testid="towers-title"
              className="font-heading mt-5 text-3xl font-bold leading-tight tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
            >
              {c.title}
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-600 md:text-base">
              {c.desc}
            </p>
            <ul className="mt-6 space-y-2.5">
              {c.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle
                    size={16}
                    weight="fill"
                    className="mt-0.5 shrink-0 text-blue-600"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* Team certifications block */}
            <div
              data-testid="towers-certifications"
              className="mt-7 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/70 p-5 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} weight="fill" className="text-amber-600" />
                <span className="font-mono text-sm font-bold uppercase tracking-[0.22em] text-amber-700 md:text-base">
                  {c.certs_title}
                </span>
              </div>
              <ul className="mt-3 space-y-2">
                {CERTIFICATIONS.map((cert) => (
                  <li
                    key={cert.code}
                    className="flex items-start gap-3 text-sm leading-snug text-slate-800"
                  >
                    <span className="mt-0.5 inline-flex shrink-0 items-center rounded-md bg-amber-500/95 px-2 py-0.5 font-mono text-[11px] font-black tracking-wider text-slate-900 shadow-sm">
                      {cert.code}
                    </span>
                    <span>
                      <span className="font-semibold">
                        {cert.long[lang] || cert.long.pt}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-amber-200 pt-3 text-sm font-medium leading-relaxed text-slate-800 md:text-base">
                {c.certs_note}
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/?produto=Torres%20e%20Mastros&marca=TVCabSat#contact"
                data-testid="towers-cta-quote"
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 active:scale-95"
              >
                {c.cta_quote}
                <ArrowRight size={14} weight="bold" />
              </Link>
              {showDetailCta && (
                <Link
                  to="/infraestruturas"
                  data-testid="towers-cta-detail"
                  className="inline-flex items-center gap-2 rounded-full border border-blue-300 bg-white px-5 py-3 text-sm font-semibold text-blue-700 transition-all hover:bg-blue-50"
                >
                  {c.cta_detail}
                </Link>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-12">
              {TILES.map((tile) => {
                const caption =
                  tile.height != null
                    ? `${tile.height}m`
                    : tile.label[lang] || tile.label.pt;
                const alt =
                  tile.height != null
                    ? c.gallery_alt(tile.height)
                    : caption;
                return (
                  <figure
                    key={tile.src}
                    data-testid={`tower-tile-${caption.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`relative overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg ${tile.span}`}
                  >
                    <div className={`${tile.aspect || "aspect-[3/4]"} w-full`}>
                      <img
                        src={tile.src}
                        alt={alt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                    <figcaption className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-slate-900/85 via-slate-900/40 to-transparent px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                      <CellTower size={11} weight="fill" />
                      {caption}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TowersShowcase;
